import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AppShell } from '@/components/AppShell';
import { useApproval, useApprovalActions } from './state';
import { useFiling, useFilingActions } from '@/features/filing/state';

export function ApprovalQueuePage() {
  const approval = useApproval((snapshot) => snapshot);
  const filingsSnapshot = useFiling((snapshot) => snapshot);
  const filingActions = useFilingActions();
  const approvalActions = useApprovalActions();
  const filings = filingsSnapshot.context.filings;
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (filingsSnapshot.matches('idle')) filingActions.loadFilings();
  }, [filingActions, filingsSnapshot]);
  useEffect(() => {
    if (!selected && filings[0]?.filingId) setSelected(filings[0].filingId);
  }, [filings, selected]);

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl space-y-8 px-6 py-10">
        <header className="border-b border-border pb-6">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Decision register</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">Approval queue</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Record an accountable approval for a filing that is ready to move forward.
          </p>
        </header>

        {filingsSnapshot.matches('loading') && (
          <p className="border border-border bg-card p-6 text-sm text-muted-foreground">Loading filing register…</p>
        )}
        {filingsSnapshot.matches('error') && (
          <p className="border border-destructive/40 bg-card p-6 text-sm">The filing register could not be loaded.</p>
        )}
        {filingsSnapshot.matches('ready') && filings.length === 0 && (
          <div className="border border-dashed border-border p-10">
            <p className="font-heading text-lg">No filings await approval</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Approved work will appear here once a filing is available.
            </p>
          </div>
        )}
        {filingsSnapshot.matches('ready') && filings.length > 0 && (
          <section className="border border-border bg-card p-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Selected filing</p>
                <label htmlFor="approval-filing-id" className="sr-only">
                  Filing to approve
                </label>
                <select
                  id="approval-filing-id"
                  data-jr-key="approval-filing-id"
                  value={selected}
                  onChange={(event) => setSelected(event.target.value)}
                  className="mt-2 min-w-64 border border-border bg-background px-3 py-2 text-sm"
                >
                  {filings.map((filing) => (
                    <option key={filing.filingId} value={filing.filingId}>
                      {filing.filingId}
                    </option>
                  ))}
                </select>
                <Badge variant="outline" className="mt-3">
                  {filings.find((filing) => filing.filingId === selected)?.status ?? 'Pending'}
                </Badge>
              </div>
              <Button
                data-jr-key="record-approval"
                disabled={!selected || approval.matches('recording')}
                onClick={() => approvalActions.record(selected)}
              >
                {approval.matches('recording') ? 'Recording…' : 'Record approval'}
              </Button>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
